import { Sha256 } from '@aws-crypto/sha256-js';
import { _Object, AbortMultipartUploadCommand, CommonPrefix, CreateMultipartUploadCommand, S3Client } from '@aws-sdk/client-s3';
import { formatUrl } from '@aws-sdk/util-format-url';
import { SignatureV4 } from '@smithy/signature-v4';
import { t } from 'i18next';
import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as NewFolder } from '../../../assets/img/storage/actions-new-folder.svg';
import { ReactComponent as ArrowBack } from '../../../assets/img/storage/arrow-back.svg';
import EmptyMain from '../../../assets/img/storage/empty-main.png';
import { ReactComponent as AnyIcon } from '../../../assets/img/storage/filter-type-any.svg';
import { ReactComponent as ArchiveIcon } from '../../../assets/img/storage/filter-type-archive.svg';
import { ReactComponent as AudioIcon } from '../../../assets/img/storage/filter-type-audio.svg';
import { ReactComponent as FolderIcon } from '../../../assets/img/storage/filter-type-folder.svg';
import { ReactComponent as PdfIcon } from '../../../assets/img/storage/filter-type-pdf.svg';
import { ReactComponent as TextIcon } from '../../../assets/img/storage/filter-type-text.svg';
import { ReactComponent as VideoIcon } from '../../../assets/img/storage/filter-type-video.svg';
import { ReactComponent as CreateFolder } from '../../../assets/svg/CreateFolder.svg';
import { ReactComponent as PlusIcon } from '../../../assets/svg/Plus2.svg';
import { ReactComponent as Upload } from '../../../assets/svg/UploadFile.svg';
import { ReactComponent as UploadFolder } from '../../../assets/svg/UploadFolder.svg';
import { useAlert } from '../../../components/alert';
import { extractErrorDetail, extractS3Error, formatBytes, ROLE, toMplatBackendDate } from '../../../components/common';
import { BreadcrumbItemProps } from '../../../components/mplat/Breadcrumb/Breadcrumb';
import Button from '../../../components/mplat/Button/Button';
import { CheckBox } from '../../../components/mplat/CheckBox';
import { Modal } from '../../../components/mplat/Modal';
import { Spinner } from '../../../components/mplat/Spinner';
import TextInput from '../../../components/mplat/TextInput';
import { useUploadContext } from '../../../contexts/UploadContext';
import { OperationUnit, UnitActions } from '../../../contexts/UploadContextProvider';
import { useAuth, useOutsideAlerter, useRole } from '../../../hooks';
import { ReactComponent as AccessIcon } from '../../../img/storage/list-access.svg';
import CheckOk from '../../../img/storage/list-reports-ok.svg';
import { ReactComponent as ShareIcon } from '../../../img/storage/list-share.svg';
import { extractPrefix } from '../../../pages/ContentEditorPage';
import { useAccountLoginMutation } from '../../../services/api';
import { BASE_URL } from '../../../services/reauth';
import { AuditOpType, SDN_OPERATION_TYPES, SdnLinkMin, SdnMeta, SdnOperationCode, SdnOperationMessageActionRequired, SdnOperationMessageParamsListZip, SdnOperationTaskStatus, SdnOperationType, SdnPermission, SdnPermissionType, useCreateSdnOperationMutation, useGetSdnDisksQuery, useGetSdnMetadataMutation, useGetSdnObjectsPermissionMutation, useGetSdnPrefixesAuditMutation, useLazyGetSdnLinksQuery, useLazyGetSdnOperationQuery, useLazyGetSdnPermissionsQuery } from '../../../services/sdn';
import MyHttpHandler from '../../../utils/MyHttpHandler';
import { useMediaQuery } from '../../Home/components/TemporalBanner';
import { bucketObjectsList, uploadFile, uploadFilePartial } from '../services';
import { createPreSignUrl, deleteFiles, downloadFile } from '../services/operations';
import { OperationModalType, SHOW_MODE, StorageItem } from '../types';
import StorageAccessModal from './StorageAccessModal';
import StorageActions from './StorageActions';
import StorageBreadcrumb from './StorageBreadcrumb';
import StorageFilters, { StorageFilterItem, StorageSearcher, StorageSorter, StorageView } from './StorageFilters';
import StorageFiltersModal, { StorageFiltersMobileGroup } from './StorageFiltersModal';
import StorageGrid from './StorageGrid';
import StorageList from './StorageList';
import { StorageDeleteConfirmModal, StorageLinkModal, StorageListZipModal, StorageReplaceFileModal, StorageReplyModal, StorageReportModal, StorageVideoModal } from './StorageModals';
import StorageNotifications from './StorageNotifications';

export const PDF_EXT = ['.PDF'];
export const VIDEO_EXT = ['AVI', 'MP4', 'MPG', 'MPEG', 'MOV', 'MKV', 'MXF', 'HLS', 'FLV', 'WEBM', 'OGG'];
export const AUDIO_EXT = ['MP3', 'WAV', 'WMA', 'AAC'];
export const TEXT_EXT = ['.TXT'];
export const ARCHIVE_EXT = ['.ZIP'];
export const IMAGES = ['.png', '.jpg', '.jpeg', '.webp'];
export const PLAYED = ['.ogg', '.mp4', '.webm', '.mkv'];

export const shareAvailRoles = [
    ROLE.SITE_ADMIN,
    ROLE.SITE_EDITOR,
    ROLE.PROVIDER_OWNER,
    ROLE.PROVIDER_ADMIN,
    ROLE.CONSUMER_OWNER,
    ROLE.CONSUMER_ADMIN,
    ROLE.PARTNER_MODERATOR_OWNER,
    ROLE.PARTNER_MODERATOR_ADMIN,
    ROLE.PARTNER_TVSRC_OWNER,
    ROLE.PARTNER_TVSRC_ADMIN,
    ROLE.PARTNER_TVSRC_MDLG_OWNER,
    ROLE.PARTNER_TVSRC_MDLG_ADMIN,
];

export function isVideo(name: string) {
    return VIDEO_EXT.some((ext) => name.toUpperCase().endsWith(ext));
}

export function isAudio(name: string) {
    return AUDIO_EXT.some((ext) => name.toUpperCase().endsWith(ext));
}

export function isArchive(name: string) {
    return ARCHIVE_EXT.some((ext) => name.toUpperCase().endsWith(ext));
}

export function sortStorageBy(key: string) {
    const zeroDate = new Date(0);

    switch (key) {
        case 'name':
            return (a: StorageItem, b: StorageItem) => a.name > b.name ? 1 : (a.name < b.name ? -1 : 0);
        case '-name':
            return (a: StorageItem, b: StorageItem) => a.name > b.name ? -1 : (a.name < b.name ? 1 : 0);
        case 'type':
            return (a: StorageItem, b: StorageItem) => {
                const posA = a.name.lastIndexOf('.');
                const posB = b.name.lastIndexOf('.');
                return a.name.slice(posA) > b.name.slice(posB) ? 1 : (a.name.slice(posA) < b.name.slice(posB) ? -1 : 0);
            }
        case '-type':
            return (a: StorageItem, b: StorageItem) => {
                const posA = a.name.lastIndexOf('.');
                const posB = b.name.lastIndexOf('.');
                return a.name.slice(posA) > b.name.slice(posB) ? -1 : (a.name.slice(posA) < b.name.slice(posB) ? 1 : 0)
            }
        case 'size':
            return (a: StorageItem, b: StorageItem) => (a.size || 0) > (b.size || 0) ? 1 : ((a.size || 0) < (b.size || 0) ? -1 : 0);
        case '-size':
            return (a: StorageItem, b: StorageItem) => (a.size || 0) > (b.size || 0) ? -1 : ((a.size || 0) < (b.size || 0) ? 1 : 0);
        case 'date':
            return (a: StorageItem, b: StorageItem) => (a.modTime || zeroDate) > (b.modTime || zeroDate) ? 1 : ((a.modTime || zeroDate) < (b.modTime || zeroDate) ? -1 : 0);
        case '-date':
            return (a: StorageItem, b: StorageItem) => (a.modTime || zeroDate) > (b.modTime || zeroDate) ? -1 : ((a.modTime || zeroDate) < (b.modTime || zeroDate) ? 1 : 0);
        default:
            return (a: StorageItem, b: StorageItem) => a.name > b.name ? -1 : (a.name < b.name ? 1 : 0);
    }
}

export function getOperation(o: SdnOperationType | undefined) {
    return o ? t(SDN_OPERATION_TYPES[o]) : '';
}

export function getBasename(path: string) {
    let tmp = path;
    if (path.endsWith('/')) {
        tmp = path.slice(0, -1);
    }
    const index = tmp.lastIndexOf('/');
    if (index !== -1) {
        tmp = tmp.slice(index + 1);
    }
    return tmp;
}

export function getDirname(path: string) {
    let tmp = path;
    if (path.endsWith('/')) {
        tmp = path.slice(0, -1);
    }
    const index = tmp.lastIndexOf('/');
    if (index !== -1) {
        tmp = tmp.slice(0, index);
    } else {
        tmp = '';
    }
    return tmp;
}

export function s3dirname(path: string) {
    let tmp = path;
    if (path.endsWith('/')) {
        tmp = path.slice(0, -1);
    }
    const index = tmp.lastIndexOf('/');
    if (index !== -1) {
        tmp = tmp.slice(0, index + 1);
    } else {
        tmp = '';
    }
    return tmp;
}

interface StorageAddBtnItemProps {
    children: React.ReactNode
}

export function StorageAddBtnItem({ children }: StorageAddBtnItemProps) {
    return (
        <div className="storage__addbtn-list-item">
            {children}
        </div>
    );
}

interface StorageAddBtnProps {
    items: React.ReactNode[]
}

export function StorageAddBtn({ items }: StorageAddBtnProps) {
    return (
        <div className="storage__addbtn">
            <div className="storage__addbtn-btn">
                <PlusIcon />
            </div>
            <div className="storage__addbtn-list">
                {items}
            </div>
        </div>
    )
}

interface StorageModalProps {
    isOpen: boolean,
    onClose: () => void,
    title: string,
    initValue?: string,
    onConfirm: (v: string) => void,
    occupied: string[],
}

export function StorageModal({ isOpen, onClose, title, initValue, onConfirm, occupied }: StorageModalProps) {
    const [value, setValue] = useState(initValue || '');
    const [alreadyExists, setAlreadyExists] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setValue(initValue || '');
        }
    }, [isOpen, initValue]);

    useEffect(() => {
        setAlreadyExists(false);
    }, [value]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <form action="#" className="storage__popup storage__popup-new-folder">
                <div className="storage__popup-heading">
                    {title}
                </div>
                <div className="storage__popup-copy-content">
                    <TextInput className="storage__popup-new-folder-name-input" value={value} onChange={(e) => setValue(e.target.value)} containerStyle={{ maxWidth: '800px' }} />
                    {alreadyExists && <div className="storage__popup-new-folder-error">
                        <Trans>storage:NAME_ALREADY_EXISTS</Trans>.
                        <br />
                        <Trans>storage:NAME_OTHER</Trans>.
                    </div>}
                </div>
                <div className="storage__popup-cta">
                    <Button onClick={() => {
                        if (occupied.includes(value)) {
                            setAlreadyExists(true);
                        } else {
                            onConfirm(value);
                        }
                    }}>
                        <Trans>Save</Trans>
                    </Button>
                </div>
            </form>
        </Modal>
    )
}

interface StorageFolderSelectionProps {
    client: S3Client,
    bucketname: string,
    isOpen: boolean,
    onClose: () => void,
    initPath?: string,
    forbidUp?: boolean,
    mode?: OperationModalType,
    prefix: string[],
    onAction: (path: string) => void,
}

export function StorageFolderSelection({ client, bucketname, isOpen, onClose, initPath, forbidUp, mode = 'copy', prefix, onAction }: StorageFolderSelectionProps) {
    const alert = useAlert();
    const wrapperRef = useRef(null);
    const newRef = useRef<HTMLDivElement>(null);

    const [fetchMeta] = useGetSdnMetadataMutation();

    const [cache, setCache] = useState<Record<string, string[]>>({});
    const [parent, setParent] = useState(initPath || '');
    // const [parents, setParents] = useState<string[]>([]);
    const [showNew, setShowNew] = useState(false);
    const [folders, setFolders] = useState<string[]>([]);
    const [isFetched, setIsFetched] = useState(false);
    const [cardObjects, setCardObjects] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (initPath) {
            setParent(initPath);
        }
    }, [initPath])

    useEffect(() => {
        if (bucketname) {
            fetchMeta({ bucketname: [bucketname], extension: ['card-system-folder'], recursive: true })
                .unwrap()
                .then((rrr) => {
                    const tmp = rrr.results.reduce((acc, curr) => {
                        const fname = s3dirname(curr.filename.slice(1));
                        acc[fname] = curr.cards.length > 0;
                        return acc;
                    }, {} as Record<string, boolean>);
                    setCardObjects(tmp);
                })
                .catch((err) => console.warn(err));
        }
    }, [bucketname]);

    useEffect(() => {
        if (bucketname) {
            const path = decodeURIComponent(parent ? (parent.endsWith('/') ? parent : (parent + '/')) : '');
            if (cache[path]) {
                setFolders(cache[path]);
            } else {
                setIsFetched(true);
                bucketObjectsList(bucketname, path, client)
                    .then((r) => {
                        const fs = r.CommonPrefixes?.map(cp => cp.Prefix || '') || [];
                        setCache((prev) => {
                            const tmp = { ...prev };
                            tmp[path] = fs;
                            return tmp;
                        });
                        setFolders(fs);
                        setIsFetched(false);
                    })
                    .catch((e) => {
                        if (e.name) {
                            alert.error(e?.message, e.name);
                        } else {
                            alert.error(extractErrorDetail(e));
                        }
                        setFolders([]);
                        setIsFetched(false);
                    });
            }
        }
    }, [bucketname, parent]);

    useOutsideAlerter(wrapperRef, () => {
        const text = newRef.current?.innerText;
        if (text) {
            setFolders([...folders, `${parent}${text}/`]);
            setCache((prev) => {
                const tmp = { ...prev };
                tmp[parent] = [...folders, `${parent}${text}/`];
                return tmp;
            });
        }
        setShowNew(false);
    });

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="storage__popup storage__popup-copy">
                <div className="storage__popup-heading">
                    {(mode === 'copy' || mode === 'copy-in-my') ?
                        (prefix.length > 1 ? t('storage:WHERE_TO_COPY_FILES') : t('storage:WHERE_TO_COPY_FILE'))
                        :
                        (mode === 'move' ? (
                            prefix.length > 1 ? t('storage:WHERE_TO_MOVE_FILES') : t('storage:WHERE_TO_MOVE_FILE')
                        ) : (mode === 'unzip' ? t('storage:WHERE_TO_EXTRACT') : t('storage:CHOOSE_FOLDER')))
                    }
                </div>
                {prefix.length === 1 && <p className="storage__popup-heading-sub">{prefix[0]}</p>}
                <div className="storage__popup-copy-content">
                    {isFetched && <Spinner />}
                    <div className="storage__popup-copy-body">
                        <div className="storage__popup-copy-body-header">
                            <div className="storage__popup-copy-body-header-heading">
                                {parent ?
                                    <span
                                        className="storage__popup-copy-body-header-back"
                                        onClick={() => {
                                            const tmp = s3dirname(parent);
                                            if (initPath && forbidUp) {
                                                if (tmp.includes(initPath)) {
                                                    setParent(tmp);
                                                }
                                            } else {
                                                setParent(tmp);
                                            }
                                        }}
                                    >
                                        <ArrowBack />
                                        {parent.endsWith('/') ? parent.slice(0, -1) : parent}
                                    </span>
                                    :
                                    <Trans>storage:MY</Trans>
                                }
                            </div>
                        </div>
                        <div className="storage__popup-copy-body-content">
                            {folders.length === 0 && !showNew && (
                                <div className="storage__popup-copy-body">
                                    <div className="storage__popup-copy-body-content" />
                                </div>
                            )}
                            {folders.map((folder) => (
                                <div key={folder} className={`storage__popup-copy-item _folder ${cardObjects[folder] ? '_mediabaza' : ''}`} onClick={() => { setParent(folder); }}>
                                    <div className="storage__popup-copy-item-icon" />
                                    <div className="storage__popup-copy-item-name">{getBasename(folder)}</div>
                                </div>
                            ))}
                            {showNew &&
                                <div ref={wrapperRef} className="storage__popup-copy-item _folder _new">
                                    <div className="storage__popup-copy-item-icon" onClick={() => {
                                        const text = newRef.current?.innerText;
                                        if (text) {
                                            setFolders([...folders, `${parent}${text}/`]);
                                            setCache((prev) => {
                                                const tmp = { ...prev };
                                                tmp[parent] = [...folders, `${parent}${text}/`];
                                                return tmp;
                                            });
                                        }
                                        setShowNew(false);
                                        setParent(`${parent}${text}/`)
                                    }} />
                                    <div
                                        ref={newRef}
                                        tabIndex={0}
                                        className="storage__popup-copy-item-name"
                                        contentEditable="true"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const text = newRef.current?.innerText;
                                                if (text) {
                                                    setFolders([...folders, `${parent}${text}/`]);
                                                    setCache((prev) => {
                                                        const tmp = { ...prev };
                                                        tmp[parent] = [...folders, `${parent}${text}/`];
                                                        return tmp;
                                                    });
                                                }
                                                setShowNew(false);
                                            } else if (e.key === 'Escape') {
                                                setShowNew(false);
                                            }
                                        }}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div className="storage__popup-cta">
                    <Button onClick={() => {
                        onAction(parent);
                    }}>
                        <Trans>{(mode === 'copy' || mode === 'copy-in-my') ?
                            'storage:COPY_TO' : (mode === 'move' ?
                                'storage:MOVE' : (mode === 'unzip' ? t('storage:EXTRACT') : t('Choose')))}</Trans>
                    </Button>
                    <button
                        className="storage__popup-cta-link"
                        onClick={() => { setShowNew(true); setTimeout(() => { newRef.current?.focus(); }, 125); }}
                    >
                        <NewFolder /> <Trans>storage:NEW_FOLDER</Trans>
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function StorageMyNotPermitted() {
    return (
        <div className="storage__main-inner storage__empty">
            <div className="storage__empty-item">
                <div className="storage__empty-item-icon">
                    <img src={EmptyMain} alt="" />
                </div>
                <p>
                    <strong>
                        <Trans>storage:EMPTY_MESSAGE_MY</Trans>
                    </strong>
                </p>
                <p>
                    <Trans>storage:SELECT_TARIFF</Trans>
                </p>
                <p className="storage__empty-item-cta">
                    <Link to="/tariff" className="btn _green">
                        <Trans>Select tariff</Trans>
                    </Link>
                </p>
            </div>
        </div>
    )
}

function StorageMyEmpty() {
    return (
        <div className="storage__empty-item">
            <div className="storage__empty-item-icon">
                <img src={EmptyMain} alt="" />
            </div>
            <p>
                <strong>
                    <Trans>storage:EMPTY_MESSAGE_MY</Trans>
                </strong>
            </p>
        </div>
    )
}

interface StorageMyProps {
    client: S3Client,
    httpHandler: MyHttpHandler,
    enabled: boolean,
}

// TODO разобраться почему при тормозах S3 показывает старое состояние
const StorageMy = React.memo(function BucketObjects({ client, enabled, httpHandler }: StorageMyProps) {
    const alert = useAlert();
    const navigate = useNavigate();
    const { account, refreshToken, user } = useAuth();
    const { prefix } = useParams();
    const [params] = useSearchParams();
    const inputMultiRef = useRef<HTMLInputElement | null>(null);
    const isMobile = useMediaQuery('(max-width: 1000px)');
    const role = useRole();

    const { data: sdnDisks } = useGetSdnDisksQuery();

    const [fetchSdnOperation] = useLazyGetSdnOperationQuery();
    const [fetchMeta] = useGetSdnMetadataMutation();
    const [createOperation] = useCreateSdnOperationMutation();
    const [fetchPermissions, { data: sdnPermissions }] = useLazyGetSdnPermissionsQuery();
    const [getSdnObjectsPermission] = useGetSdnObjectsPermissionMutation();
    const [fetchSdnLinks, { data: sdnLinks }] = useLazyGetSdnLinksQuery();
    const [getDownloadCounters] = useGetSdnPrefixesAuditMutation();
    const [accountLogin] = useAccountLoginMutation();

    const [isFetched, setIsFetched] = useState(false);

    const [needReply, setNeedRelpy] = useState<SdnOperationMessageActionRequired & { opId: string } | undefined>(undefined);
    const [sort, setSort] = useState<string>('name');
    const [mode, setMode] = useState(localStorage.showSdnMode || SHOW_MODE.GRID);
    const [isOpenCreateFolder, setIsOpenCreateFolder] = useState(false);
    const [isOpenRenameFile, setIsOpenRenameFile] = useState(false);
    const [isOpenZipName, setIsOpenZipName] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [accessPath, setAccessPath] = useState<string>('');
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [replaceFiles, setReplaceFiles] = useState<File[]>([]);
    const [isOpenMeta, setIsOpenMeta] = useState(false);
    const [metaReport, setMetaReport] = useState<SdnMeta | undefined>(undefined);

    const [objects, setObjects] = useState<_Object[]>([]);
    const [folders, setFolders] = useState<CommonPrefix[]>([]);
    const [storageData, setStorageData] = useState<StorageItem[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [myObjects, setMyObjects] = useState<Record<string, SdnPermissionType>>({});
    const [cardObjects, setCardObjects] = useState<Record<string, boolean>>({});
    const [metaObjects, setMetaObjects] = useState<Record<string, SdnMeta>>({});
    const [counters, setCounters] = useState<Record<string, number>>({});
    // const [pres, setPres] = useState<Record<string, string>>({});

    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemProps[]>([]);
    const [update, setUpdate] = useState(0);
    const [activeListzip, setActiveListzip] = useState(false);
    const [listzip, setListzip] = useState<SdnOperationMessageParamsListZip | null>(null);
    const [myPermissions, setMyPermissions] = useState<Record<string, SdnPermission[]>>({});
    const [availUpload, setAvailUpload] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [preSignPrefix, setPreSignPrefix] = useState('');
    const [finishedOperations, setFinishedOperations] = useState<string[]>([]);
    const [showPlayer, setShowPlayer] = useState<string>('');
    const [videoName, setVideoName] = useState<string>('');

    const { dispatch, state, operations, dispatchOperations } = useUploadContext();

    const bucketname = useMemo(() => sdnDisks?.results.find((disk) => disk.account.id === Number(account))?.bucket, [sdnDisks]);
    const thisDisk = useMemo(() => (sdnDisks?.results.find((d) => d.bucket === bucketname)), [sdnDisks, bucketname]);

    const [isOpenMobileModal, setIsOpenMobileModal] = useState(false);
    const [search, setSearch] = useState('');
    const [type, setType] = useState('');
    const [date, setDate] = useState('');
    const [sinceDate, setSinceDate] = useState(new Date());
    const [tillDate, setTillDate] = useState(new Date());
    const [access, setAccess] = useState(false);
    // const [people, setPeople] = useState<string[]>([]);
    const deferredSearch = useDeferredValue(search);

    const searchFilter = useCallback((row: StorageItem) => {
        return (!deferredSearch) || row.name.toLowerCase().indexOf(deferredSearch.toLowerCase()) !== -1;
    }, [deferredSearch]);

    const links: Record<string, SdnLinkMin[]> = useMemo(() => {
        return sdnLinks?.results.reduce((acc, curr) => {
            curr.prefixes.forEach((prfx) => {
                const key = extractPrefix(prfx);
                if (acc[key]) {
                    acc[key].push(curr);
                } else {
                    acc[key] = [curr];
                }
            });
            return acc;
        }, {} as Record<string, SdnLinkMin[]>) || {};
    }, [sdnLinks]);

    const searchHints = useMemo(() => {
        return storageData.filter(searchFilter).slice(0, 10).map((s) => ({ value: s.name, className: '' }));
    }, [storageData, deferredSearch]);

    const haveFullAccess = useMemo(() => shareAvailRoles.includes(role.label), [role.label])

    const typeFilter = useCallback((row: StorageItem) => {
        if (type === '') return true;
        if (type === 'folder') return row.folder;
        return (!type) || availTypes.find((tp) => tp.value === type)?.ext?.some((e) => row.name.toUpperCase().endsWith(e));
    }, [type]);

    const accessFilter = useCallback((row: StorageItem) => {
        return (!access) || row.shared || row.access;
    }, [access]);

    const dateFilter = useCallback((row: StorageItem) => {
        if (date) {
            if (row.modTime) {
                if (date !== 'custom') {
                    const today = new Date();
                    today.setUTCHours(0, 0, 0, 0);
                    const yesterday = new Date(today);
                    yesterday.setDate(today.getDate() - 1);
                    const last7 = new Date(today);
                    last7.setDate(today.getDate() - 7);
                    const last30 = new Date(today);
                    last30.setDate(today.getDate() - 30);
                    const year = new Date(today);
                    year.setUTCMonth(0, 1);
                    const lastyear = new Date(today);
                    lastyear.setUTCFullYear(year.getFullYear() - 1);
                    switch (date) {
                        case 'today':
                            return row.modTime > today;
                        case 'yesterday':
                            return row.modTime > yesterday;
                        case 'last7':
                            return row.modTime > last7;
                        case 'last30':
                            return row.modTime > last30;
                        case 'year':
                            return row.modTime > year;
                        case 'lastyear':
                            return row.modTime > lastyear && row.modTime < year;
                    }
                } else {
                    return row.modTime > sinceDate && row.modTime < tillDate;
                }
            }
            return false;
        }
        return true;
    }, [date, sinceDate, tillDate]);

    const getAnyPerm = useCallback((p: string | undefined): boolean => {
        if (p) {
            const perm = myObjects[p];
            return perm !== SdnPermissionType.DENY;
        }
        return false;
    }, [myObjects]);

    // const getSignUrl = useCallback((p: string) => createPreSignUrl(client, bucketname || '', p), [client, bucketname]);

    const [folderMode, setFolderMode] = useState<OperationModalType>('copy');
    const [folderPrefix, setFolderPrefix] = useState<string[]>([]);

    const availSort = [
        { label: t('storage:SORT_BY_NAME'), value: 'name' },
        { label: t('storage:SORT_BY_TYPE'), value: 'type' },
        { label: t('storage:SORT_BY_SIZE'), value: 'size' },
        { label: t('storage:SORT_BY_DATE'), value: 'date' },
    ]

    const availTypes = [
        { value: '', label: t('storage:FILE_TYPE.ANY'), icon: <AnyIcon />, },
        { value: 'folder', label: t('storage:FILE_TYPE.FOLDERS'), icon: <FolderIcon />, ext: ['/'] },
        { value: 'pdf', label: t('storage:FILE_TYPE.PDF'), icon: <PdfIcon />, ext: ['.PDF'] },
        { value: 'video', label: t('storage:FILE_TYPE.VIDEO'), icon: <VideoIcon />, ext: ['AVI', 'MP4', 'MPG', 'MPEG', 'MOV', 'MKV', 'MXF', 'HLS', 'FLV', 'WEBM'] },
        { value: 'audio', label: t('storage:FILE_TYPE.AUDIO'), icon: <AudioIcon />, ext: ['MP3', 'WAV', 'WMA', 'AAC'] },
        { value: 'texts', label: t('storage:FILE_TYPE.TEXTS'), icon: <TextIcon />, ext: ['.TXT'] },
        { value: 'archives', label: t('storage:FILE_TYPE.ARCHIVES'), icon: <ArchiveIcon />, ext: ['.ZIP'] },
    ]

    const availDates = [
        { value: '', label: t('storage:FILE_MODIFY_DATE.ANY') },
        { value: 'today', label: t('storage:FILE_MODIFY_DATE.TODAY') },
        { value: 'yesterday', label: t('storage:FILE_MODIFY_DATE.YESTERDAY') },
        { value: 'last7', label: t('storage:FILE_MODIFY_DATE.LAST7') },
        { value: 'last30', label: t('storage:FILE_MODIFY_DATE.LAST30') },
        { value: 'year', label: t('storage:FILE_MODIFY_DATE.YEAR') },
        { value: 'lastyear', label: t('storage:FILE_MODIFY_DATE.LASTYEAR') },
    ];

    // const availPeople = [
    //     { value: 'a', label: 'b', custom: 'c' },
    //     { value: 'a2', label: 'b2', custom: 'c2' },
    //     { value: 'a3', label: 'b3', custom: 'c3' },
    //     { value: 'a4', label: 'b4', custom: 'c4' },
    // ];

    useEffect(() => {
        localStorage.showSdnMode = mode;
    }, [mode]);

    useEffect(() => {
        if (thisDisk) {
            getSdnObjectsPermission({ disk: thisDisk?.disk_id, files: prefix ? ['/' + prefix] : ['/'] })
                .unwrap()
                .then((rrr) => {
                    const perm = rrr.results.at(0)?.perm || 0;
                    setAvailUpload(!thisDisk.write_locked && (perm & SdnPermissionType.WRITE) === SdnPermissionType.WRITE);
                })
                .catch(() => {
                    setAvailUpload(false);
                });
        }
    }, [prefix, thisDisk]);

    useEffect(() => {
        const files: string[] = storageData.map((o) => '/' + o.prefix);
        if (files.length > 0) {
            getSdnObjectsPermission({ disk: thisDisk?.disk_id, files: files })
                .unwrap()
                .then((rrr) => {
                    const tmp = rrr.results.reduce((acc, curr) => {
                        const fname = curr.filename.slice(1);
                        acc[fname] = curr.perm;
                        return acc;
                    }, {} as Record<string, SdnPermissionType>);
                    setMyObjects(tmp);
                    // const perm = rrr.results.at(0)?.perm || 0;
                    // setAvailUpload((perm & SdnPermissionType.WRITE) === SdnPermissionType.WRITE);
                })
                .catch((e) => {
                    setMyObjects({});
                    alert.error(extractErrorDetail(e));
                });
            const prefixes = storageData.map((o) => `${bucketname}/${o.prefix}`);
            getDownloadCounters({ prefixes: prefixes })
                .unwrap()
                .then((ops) => {
                    const tmp = ops.results
                        .filter((o) => o.operation === AuditOpType.GET)
                        .reduce((acc, curr) => {
                            acc[curr.prefix] = curr.opcount;
                            return acc;
                        }, {} as Record<string, number>);
                    setCounters(tmp)
                })
                .catch((err) => console.warn(err))
        } else {
            setMyObjects({});
        }
    }, [storageData, bucketname, thisDisk]);

    useEffect(() => {
        const dropZone = document.getElementsByClassName('main-wrapper')[0];

        const ondragenter = (e: Event) => {
            e.preventDefault();
            dropZone.className = 'main-wrapper _drop-zone';
        };

        const ondragover = (e: Event) => {
            e.preventDefault();
            dropZone.className = 'main-wrapper _drop-zone';
        };

        const ondragleave = (e: Event) => {
            e.preventDefault();
            setTimeout(function () {
                dropZone.className = 'main-wrapper';
            }, 100);
        };

        async function traverseFileTree(entry: FileSystemEntry): Promise<File[]> {
            const files: File[] = [];

            if (entry.isFile) {
                // If it's a file entry, get the actual File object
                const file = await new Promise<File>((resolve, reject) => {
                    (entry as FileSystemFileEntry).file(resolve, reject);
                });
                files.push(file);
            } else if (entry.isDirectory) {
                // If it's a directory, read its entries and traverse them
                const dirReader = (entry as FileSystemDirectoryEntry).createReader();
                const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
                    // Note: readEntries might need to be called multiple times for large directories in some browsers
                    dirReader.readEntries(resolve, reject);
                });

                for (const subEntry of entries) {
                    const subFiles = await traverseFileTree(subEntry);
                    files.push(...subFiles);
                }
            }

            return files;
        }

        const ondrop = async (e: Event) => {
            e.preventDefault();
            const de = e as DragEvent;

            if (!de.dataTransfer?.items) {
                return;
            }

            const items = de.dataTransfer?.items;

            const files: File[] = [];

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.kind === 'file') {
                    // Use type assertion to access the webkitGetAsEntry method
                    const entry = item.webkitGetAsEntry();
                    if (entry) {
                        // Recursively traverse the entry and collect files
                        const entryFiles = await traverseFileTree(entry);
                        files.push(...entryFiles);
                    }
                }
            }

            dropZone.className = 'main-wrapper';
            const fileList = files; // Array.from(de.dataTransfer?.files || []);
            const double = fileList.filter((f) => storageData.some((s) => s.name === (f.webkitRelativePath || f.name)));

            if (double.length > 0) {
                setShowReplaceModal(true);
                setReplaceFiles(double);
                uploadFiles(fileList.filter((f) => double.every((d) => d.name !== f.name)), true);
            } else {
                uploadFiles(fileList, true);
            }
        };

        dropZone.addEventListener('dragenter', ondragenter);
        dropZone.addEventListener('dragover', ondragover);
        dropZone.addEventListener('dragleave', ondragleave);
        dropZone.addEventListener('drop', ondrop);

        return () => {
            dropZone.removeEventListener('dragenter', ondragenter);
            dropZone.removeEventListener('dragover', ondragover);
            dropZone.removeEventListener('dragleave', ondragleave);
            dropZone.removeEventListener('drop', ondrop);
        }
    }, [client, httpHandler, prefix, bucketname, storageData, dispatch]);

    useEffect(() => {
        const ops = operations.filter((o) => o.status < SdnOperationTaskStatus.RETRY).map((o) => o.name);
        if (ops.some((o) => selected.includes(o))) {
            setSelected((p) => p.filter((s) => !ops.includes(s)));
        }
    }, [selected, operations]);

    const addItems = [
        <StorageAddBtnItem key={1}>
            <button className="storage__addbtn-item" onClick={() => setIsOpenCreateFolder(true)}>
                <CreateFolder />
                <Trans>Create folder</Trans>
            </button>
        </StorageAddBtnItem>,
        <StorageAddBtnItem key={2}>
            <label className="storage__addbtn-item">
                <input type="file" multiple onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                        const fileList = Array.from(e.target.files);
                        const double = fileList.filter((f) => storageData.some((s) => s.name === f.name));

                        if (double.length > 0) {
                            setShowReplaceModal(true);
                            setReplaceFiles(double);
                            uploadFiles(fileList.filter((f) => double.every((d) => d.name !== f.name)), true);
                        } else {
                            uploadFiles(fileList, true);
                        }

                    }
                    // chrome fix
                    e.target.value = '';
                    e.target.files = null;
                }} />
                <Upload />
                <Trans>Upload</Trans>
            </label>
        </StorageAddBtnItem>,
        <StorageAddBtnItem key={3}>
            <label className="storage__addbtn-item">
                <input
                    ref={inputMultiRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        if (e.target.files) {
                            uploadFiles(Array.from(e.target.files), true);
                        }
                        // chrome fix
                        e.target.value = '';
                        e.target.files = null;
                    }}
                    {...{
                        directory: '',
                        webkitdirectory: '',
                        multiple: true,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any}
                />
                <UploadFolder />
                <Trans>storage:UPLOAD_FOLDER</Trans>
            </label>
        </StorageAddBtnItem>
    ];
    const addBtn = <StorageAddBtn items={addItems} />

    const onDownloadZip = useCallback(async (s: string) => {
        accountLogin({
            refresh: refreshToken,
            username: user,
            account: account,
        }).unwrap().then(async (payload) => {
            const jwtSigning = payload.signing?.split('.')[1];
            const jwtSigningDecoded = jwtSigning ? JSON.parse(atob(jwtSigning)) : {};

            const accessKeyId = jwtSigningDecoded?.key;
            const secretAccessKey = jwtSigningDecoded?.secret;

            const key = 'be/api/v1/sdn/archive/zip/';
            let host = location.host;
            let port = location.port;
            const expiresIn = 300; // 5 минут (секунды)
            if (BASE_URL !== '/') {
                host = BASE_URL.split('//')[1]?.split(':')[0];
                port = BASE_URL.slice(0, -1).split('//')[1]?.split(':')[1];
            }
            const request = {
                method: 'GET',
                protocol: location.protocol,
                path: `/${key}`,
                query: {
                    filename: getBasename(s) + '.zip',
                    prefixes: [`${bucketname}/${s}`],
                },
                headers: {
                    'x-amz-content-sha256': 'UNSIGNED-PAYLOAD',
                    host: `${host}${port ? ':' + port : ''}`,
                },
                hostname: host,
                port: port ? Number(port) : undefined,
            };

            // 2. Создаём Signer
            const signer = new SignatureV4({
                credentials: { accessKeyId, secretAccessKey, sessionToken: '' },
                region: 'EU',
                service: 'execute-api',
                sha256: Sha256,
            });

            // 3. Генерируем pre-signed URL
            const url = await signer.presign(request, { expiresIn });

            window.open(formatUrl(url));

        }).catch((e) => {
            alert.error(e);
        });
        // console.log('Pre-signed URL:', accessKeyId, secretAccessKey, url, formatUrl(url));
    }, [bucketname, refreshToken, account, user]);

    const uploadFiles = useCallback((files: File[], replace: boolean) => {
        files.forEach((file) => {
            let filename = file.webkitRelativePath || file.name;
            if (!replace) {
                const dotIndex = filename.lastIndexOf('.');
                if (dotIndex !== -1) {
                    let i = 1;
                    const base = filename;
                    while (storageData.some((s) => s.name === filename)) {
                        filename = base.slice(0, dotIndex) + `_${i}` + base.slice(dotIndex);
                        i += 1;
                    }
                } else {
                    let i = 1;
                    const base = filename;
                    while (storageData.some((s) => s.name === filename)) {
                        filename = base + `_${i}`;
                        i += 1;
                    }
                }
            }
            if (file.size < 40 * 1024 * 1024) {
                const abortController = new AbortController();
                dispatch({ type: 'added', started: new Date(), size: file.size, name: prefix ? prefix + filename : filename, progress: 0, bucket: bucketname || '', abortController });
                // setName(file.name);
                uploadFile(client, bucketname, prefix, file, filename, httpHandler, (path, p) => {
                    if (p === 100) setTimeout(() => dispatch({ type: 'changed', size: file.size, name: path, progress: p, bucket: bucketname || '', finished: p === 100 }), 2000);
                    dispatch({ type: 'changed', size: file.size, name: path, progress: p, bucket: bucketname || '' })
                }, abortController.signal)
                    .then(() => setUpdate((p) => p + 1))
                    .catch((e) => {
                        if (e.name === 'AbortError') {
                            console.log(e);
                        } else {
                            alert.error(t(extractS3Error(e)));
                        }
                    });
            } else {
                const abortController = new AbortController();
                const input = {
                    Bucket: bucketname,
                    Key: prefix ? prefix + filename : filename
                };
                const command = new CreateMultipartUploadCommand(input);
                dispatch({ type: 'added', started: new Date(), size: file.size, name: prefix ? prefix + filename : filename, progress: 0, bucket: bucketname || '', abortController });

                client.send(command).then((r) => {
                    uploadFilePartial(client, bucketname, file, prefix, filename, r.UploadId, httpHandler, (path, p) => {
                        dispatch({ type: 'changed', size: file.size, name: path, progress: p, bucket: bucketname || '' });
                        if (p === 100) setTimeout(() => dispatch({ type: 'changed', size: file.size, name: path, progress: p, bucket: bucketname || '', finished: p === 100 }), 2000);
                    }, abortController.signal)
                        .then(() => setUpdate((p) => p + 1))
                        .catch((e) => {
                            if (e.name === 'AbortError' && e.reason === 'handle') {
                                const abortCommand = new AbortMultipartUploadCommand({
                                    Bucket: bucketname,
                                    Key: prefix ? prefix + filename : filename,
                                    UploadId: r.UploadId,
                                });
                                client.send(abortCommand);
                            } else if (e.name !== 'AbortError') {
                                alert.error(t(extractS3Error(e)));
                            }
                        });
                }).catch((err) => alert.error(t(extractS3Error(err))));
            }
        });
    }, [bucketname, prefix, httpHandler, storageData, dispatch]);

    const renameFileOperation = useCallback((src: string, dst: string) => {
        const isFolder = src.endsWith('/');
        const dirName = s3dirname(src);
        const srcPath = `${thisDisk?.bucket}/${src}`;

        createOperation({
            command: SdnOperationType.RENAME,
            source: srcPath,
            destination: isFolder ? `${thisDisk?.bucket}/${dirName}${dst}/` : `${thisDisk?.bucket}/${dirName}${dst}`,
            patterns: [],
            timeout: 600,
        }).unwrap().then((z) => {
            const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: src, progress: 0, operation: SdnOperationType.RENAME, src: z.results.metadata.source, dst: z.results.metadata.destination };
            dispatchOperations(op);
            operationWatchDog(op);
        }).catch((z) => alert.error(extractErrorDetail(z)));
    }, [bucketname, thisDisk]);

    // const copyFileOperation = useCallback((src: string, dst: string) => {
    //     const isFolder = src.endsWith('/');
    //     const srcPath = `${thisDisk?.bucket}/${src}`;

    //     createOperation({
    //         command: SdnOperationType.COPY,
    //         source: srcPath,
    //         destination: isFolder ? `${thisDisk?.bucket}/${dst}${getBasename(src)}/` : `${thisDisk?.bucket}/${dst}${getBasename(src)}`,
    //         patterns: [],
    //         timeout: 600,
    //     }).unwrap().then((z) => {
    //         const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: src, progress: 0, operation: SdnOperationType.COPY, src: z.results.metadata.source, dst: z.results.metadata.destination };
    //         dispatchOperations(op);
    //         operationWatchDog(op);
    //     }).catch((z) => alert.error(extractErrorDetail(z)));
    // }, [bucketname]);

    const copyFilesOperation = useCallback((src: string[], dst: string) => {
        const srcPaths = src.map((s) => `${thisDisk?.bucket}/${s}`);
        const name = s3dirname(src[0]) || '/';

        createOperation({
            command: SdnOperationType.COPY,
            sources: srcPaths,
            destination: `${thisDisk?.bucket}/${dst}`,
            patterns: [],
            timeout: 60000,
        }).unwrap().then((z) => {
            const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: name, size: 0, progress: 0, operation: SdnOperationType.COPY, src: z.results.metadata.source, dst: z.results.metadata.destination };
            dispatchOperations(op);
            operationWatchDog(op);
        }).catch((z) => alert.error(extractErrorDetail(z)));
    }, [bucketname, thisDisk]);

    const moveFilesOperation = useCallback((src: string[], dst: string) => {
        const srcPaths = src.map((s) => `${thisDisk?.bucket}/${s}`);
        const name = s3dirname(src[0]) || '/';

        createOperation({
            command: SdnOperationType.MOVE,
            sources: srcPaths,
            destination: `${thisDisk?.bucket}/${dst}`,
            patterns: [],
            timeout: 60000,
        }).unwrap().then((z) => {
            const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: name, size: 0, progress: 0, operation: SdnOperationType.MOVE, src: z.results.metadata.source, dst: z.results.metadata.destination };
            dispatchOperations(op);
            operationWatchDog(op);
        }).catch((z) => alert.error(extractErrorDetail(z)));
    }, [bucketname, thisDisk]);

    // const moveFileOperation = useCallback((src: string, dst: string) => {
    //     const isFolder = src.endsWith('/');
    //     const srcPath = `${thisDisk?.bucket}/${src}`;

    //     createOperation({
    //         command: SdnOperationType.MOVE,
    //         source: srcPath,
    //         destination: isFolder ? `${thisDisk?.bucket}/${dst}${getBasename(src)}/` : `${thisDisk?.bucket}/${dst}${getBasename(src)}`,
    //         patterns: [],
    //         timeout: 600,
    //     }).unwrap().then((z) => {
    //         const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: src, progress: 0, operation: SdnOperationType.MOVE, src: z.results.metadata.source, dst: z.results.metadata.destination };
    //         dispatchOperations(op);
    //         operationWatchDog(op);
    //     }).catch((z) => alert.error(extractErrorDetail(z)));
    // }, [bucketname, thisDisk]);

    const listZipFileOperation = useCallback((name: string) => {
        const src = `${thisDisk?.bucket}/${name}`;
        setActiveListzip(true);
        createOperation({
            command: SdnOperationType.LISTZIP,
            source: src,
            patterns: [],
            timeout: 600,
        }).unwrap().then((z) => {
            const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: src, progress: 0, operation: SdnOperationType.LISTZIP, src: src, dst: '' };
            dispatchOperations(op);
            operationWatchDog(op);
        }).catch((z) => { setActiveListzip(false); alert.error(extractErrorDetail(z)); });
    }, [bucketname, thisDisk]);

    const unZipFileOperation = useCallback((src: string, dst: string, patterns?: string[]) => {
        const srcPath = `${thisDisk?.bucket}/${src}`;

        createOperation({
            command: SdnOperationType.UNZIP,
            source: srcPath,
            destination: `${thisDisk?.bucket}/${dst}`,
            patterns: patterns || ['*'],
            timeout: 600,
        }).unwrap().then((z) => {
            const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: src, progress: 0, operation: SdnOperationType.UNZIP, src: z.results.metadata.source, dst: z.results.metadata.destination };
            dispatchOperations(op);
            operationWatchDog(op);
        }).catch((z) => alert.error(extractErrorDetail(z)));
    }, [bucketname, thisDisk]);

    const zipFileOperation = useCallback((src: string[], dst: string, patterns?: string[]) => {
        const srcPaths = src.map((s) => `${thisDisk?.bucket}/${s}`);
        const srcDir = s3dirname(src[0]);

        createOperation({
            command: SdnOperationType.ZIP,
            sources: srcPaths,
            destination: `${thisDisk?.bucket}/${srcDir}${dst}.zip`,
            patterns: patterns || ['*'],
            timeout: 60000,
        }).unwrap().then((z) => {
            const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: dst, progress: 0, operation: SdnOperationType.ZIP, src: z.results.metadata.source, dst: z.results.metadata.destination };
            dispatchOperations(op);
            operationWatchDog(op);
        }).catch((z) => alert.error(extractErrorDetail(z)));
    }, [bucketname, thisDisk]);

    const inProgress = useCallback((row: StorageItem) => {
        return operations.some((o) => o.name === row.prefix && o.progress !== 100);
    }, [operations]);

    useEffect(() => {
        const finised = operations.filter((o) => o.progress === 100).map((o) => o.name);
        const notLocal = finised.filter((f) => !finishedOperations.includes(f));
        if (notLocal.length) {
            setFinishedOperations((fo) => [...fo, ...notLocal]);
            setUpdate((p) => p + 1);
        }
    }, [operations]);

    useEffect(() => {
        const finised = state.filter((o) => o.finished).map((o) => o.name);
        const notLocal = finised.filter((f) => !finishedOperations.includes(f));
        if (notLocal.length) {
            setFinishedOperations((fo) => [...fo, ...notLocal]);
            setUpdate((p) => p + 1);
        }
    }, [state]);

    // const deleteFileOperation = useCallback((name: string) => {
    //     const ret = confirm('Delete?');
    //     if (ret) {
    //         const src = `${thisDisk?.bucket}/${name}`;
    //         createOperation({
    //             command: SdnOperationType.DELETE,
    //             source: `${thisDisk?.bucket}/${name}`,
    //             patterns: [],
    //             timeout: 600,
    //         }).unwrap().then((z) => {
    //             const op = { type: 'added' as UnitActions, id: z.results.id, status: SdnOperationTaskStatus.PENDING, name: src, progress: 0, operation: SdnOperationType.DELETE };
    //             dispatchOperations(op);
    //             operationWatchDog(op);
    //         }).catch((z) => alert.error(extractErrorDetail(z)));
    //     }
    // }, [bucketname]);

    useEffect(() => {
        if (bucketname) {
            fetchMeta({ bucketname: [bucketname], extension: ['card-system-folder'], recursive: true })
                .unwrap()
                .then((rrr) => {
                    const tmp = rrr.results.reduce((acc, curr) => {
                        const fname = s3dirname(curr.filename.slice(1));
                        acc[fname] = curr.cards.length > 0;
                        return acc;
                    }, {} as Record<string, boolean>);
                    setCardObjects(tmp);
                })
                .catch((err) => console.warn(err));
        }
    }, [bucketname]);

    useEffect(() => {
        if (bucketname) {
            setIsFetched(true);
            fetchPermissions().unwrap().then(() => setIsFetched(false)).catch(() => setIsFetched(false));
            fetchSdnLinks();

            fetchMeta({ bucketname: [bucketname], prefix: [prefix || ''], recursive: false })
                .unwrap()
                .then((rrr) => {
                    const tmp = rrr.results.reduce((acc, curr) => {
                        const fname = curr.filename.slice(1);
                        acc[fname] = curr;
                        return acc;
                    }, {} as Record<string, SdnMeta>);
                    setMetaObjects(tmp);
                })
                .catch((err) => console.warn(err));
        }
    }, [bucketname, prefix]);

    useEffect(() => {
        if (sdnPermissions?.results) {
            const tmp = sdnPermissions.results.reduce((acc, curr) => {
                const fname = curr.filename.slice(1);
                if (acc[fname]) {
                    acc[fname].push(curr);
                } else {
                    acc[fname] = [curr];
                }
                return acc;
            }, {} as Record<string, SdnPermission[]>);
            setMyPermissions(tmp);
        }
    }, [sdnPermissions]);

    // TODO превьюхи нужны, вернуть
    // useEffect(() => {
    //     const images: string[] = objects.filter((f) => IMAGES.some((ext) => f.Key?.toLowerCase().endsWith(ext)) && !pres[f.Key as string]).map((f) => f.Key as string);
    //     images.forEach((i) => getSignUrl(i).then((ret) => setPres((p) => {
    //         const tmp = { ...p };
    //         tmp[i] = ret;
    //         return tmp;
    //     })));
    // }, [objects, pres, setPres]);

    useEffect(() => {
        const tmpDirs: StorageItem[] = (folders.map((folder) => ({
            name: folder.Prefix?.slice(0, folder.Prefix.length - 1).split('/').pop() || '',
            path: `/sdn/my/${encodeURIComponent(folder.Prefix || '')}`,
            prefix: folder.Prefix,
            folder: true,
            linked: !!cardObjects[folder.Prefix || ''],
            shared: myPermissions[folder.Prefix || '']?.length > 0,
            operations: getOperation(operations.find((o) => o.name === folder.Prefix)?.operation),
            downloads: '',
            access: !!links[folder.Prefix || ''],
            check: '',
            modTime: undefined,
            size: undefined,
        })) || []);
        const tmpObjs = objects.map((obj) => ({
            name: obj.Key?.replace(prefix || '', '') || '',
            path: '',
            prefix: obj.Key,
            folder: false,
            linked: false,
            shared: myPermissions[obj.Key || '']?.length > 0,
            operations: getOperation(operations.find((o) => o.name === obj.Key)?.operation),
            downloads: '',
            access: !!links[obj.Key || ''],
            check: '',
            modTime: obj.LastModified,
            size: obj.Size,
            // preSignUrl: pres[obj.Key || ''],
        })) || [];
        tmpDirs.sort(sortStorageBy(sort));
        tmpObjs.sort(sortStorageBy(sort));
        const tmp = tmpDirs.concat(tmpObjs);
        setStorageData(tmp);
    }, [objects, folders, links, sort, /*pres,*/ operations, cardObjects]);

    useEffect(() => {
        if (bucketname) {
            const paths: string[] = [];
            setBreadcrumbs([
                {
                    href: '/sdn/my',
                    label: `${t('Disk')} ${thisDisk?.account.name}`,
                },
                ...(prefix?.split('/') || []).filter((path) => !!path).map((path) => {
                    paths.push(path);
                    return {
                        href: `/sdn/my/${paths.join('%2f')}%2f`,
                        label: path,
                    }
                })
            ]);
            if (enabled) {
                setIsFetched(true);
                bucketObjectsList(bucketname, decodeURIComponent(prefix || ''), client)
                    .then((r) => {
                        setIsFetched(false);
                        setFolders(r.CommonPrefixes || []);
                        setObjects(r.Contents || []);
                    })
                    .catch((e) => {
                        if (e.name) {
                            alert.error(e?.message, e.name);
                        } else {
                            alert.error(extractS3Error(e));
                        }
                        setIsFetched(false);
                        setFolders([]);
                        setObjects([]);
                        setStorageData([]);
                    });
            }
        }
    }, [bucketname, enabled, prefix, myPermissions, update, thisDisk]);

    const operationWatchDog = useCallback((op: OperationUnit) => {
        fetchSdnOperation(op.id).unwrap()
            .then((z) => {
                let cont = z.results.messages.length === 0;
                let newOp = { ...op, status: z.results.status, type: 'changed' as UnitActions };
                z.results.messages.forEach((msg) => {
                    if (msg.type === 'action-required') {
                        const m = msg as SdnOperationMessageActionRequired;
                        setNeedRelpy({ ...m, opId: op.id });
                    } else if (msg.type === 'info') {
                        switch (msg.code) {
                            case SdnOperationCode.PROGRESS:
                                console.log('Progress', newOp);
                                newOp = {
                                    ...newOp,
                                    type: 'changed',
                                    status: z.results.status || newOp.status,
                                    progress: ((msg.params?.transferred || 0) / (msg.params?.size || 1)) * 100,
                                };
                                cont = true;
                                break;
                            case SdnOperationCode.COMPLETED:
                                console.log('Completed', newOp);
                                newOp = {
                                    ...newOp,
                                    type: 'changed',
                                    status: SdnOperationTaskStatus.SUCCESS,
                                    progress: 100,
                                }
                                setUpdate((p) => p + 1);
                                break;
                            case SdnOperationCode.STARTED:
                                console.log('Started', newOp);
                                newOp = {
                                    ...newOp,
                                    type: 'changed',
                                    progress: 0,
                                }
                                cont = true;
                                break;
                            case SdnOperationCode.ZIP_LIST:
                                console.log('Listzip', newOp);
                                setActiveListzip(false);
                                setListzip(msg.params as SdnOperationMessageParamsListZip);
                                break;
                            case SdnOperationCode.DELETE_ERROR:
                            case SdnOperationCode.ACCESS_FAILED:
                            case SdnOperationCode.BAD_REQUEST:
                            case SdnOperationCode.CANCELLED:
                            case SdnOperationCode.COPY_ERROR:
                            case SdnOperationCode.DELETE_ERRORS:
                            case SdnOperationCode.DELETE_PROGRESS:
                            case SdnOperationCode.ERROR:
                            case SdnOperationCode.FILE_EXISTS:
                            case SdnOperationCode.FILE_NOT_DIRECTORY:
                            case SdnOperationCode.NOT_FOUND_OR_PERMISSION_DENIDED:
                            case SdnOperationCode.PERMISSION_DENIDED:
                            case SdnOperationCode.ZIP_NOT_DIRECTORY:
                            case SdnOperationCode.ZIP_NOT_FOUND: {
                                const err = `${msg.id}[${msg.code}]: ${msg.msg}`;
                                console.error(err);
                                break;
                            }
                        }
                        if (z.results.status <= SdnOperationTaskStatus.RETRY) {
                            dispatchOperations(newOp);
                        } else {
                            setTimeout(() => dispatchOperations(newOp), 2000);
                        }

                        if (z.results.status > SdnOperationTaskStatus.RETRY) {
                            if (z.results.metadata.command === SdnOperationType.LISTZIP) {
                                setActiveListzip(false);
                            }
                            setUpdate((p) => p + 1);
                        }
                    } else {
                        alert.error(`${msg.msg}\n${(msg.details && msg.details !== 'None') ? msg.details : ''}`)
                    }
                });
                if (cont) {
                    if (z.results.status <= SdnOperationTaskStatus.RETRY) {
                        setTimeout(() => operationWatchDog(newOp), 2500);
                    } else {
                        console.log('Finish', newOp);
                        setTimeout(() => dispatchOperations({
                            ...newOp,
                            type: 'changed',
                            status: z.results.status,
                        }), 2000);
                        setUpdate((p) => p + 1);
                    }
                }
            })
            .catch((e) => {
                console.log(op);
                dispatchOperations({ ...op, status: SdnOperationTaskStatus.FAILURE, type: 'changed' });
                alert.error(extractErrorDetail(e));
            });
    }, [fetchSdnOperation, setUpdate]);

    useEffect(() => {
        const selectedFile = params.get('file');
        if (selectedFile && storageData.length > 0) {
            const item = storageData.find((s) => s.prefix === selectedFile);
            if (item) {
                setSelected([selectedFile]);
                setTimeout(() => {
                    const elements = document.getElementsByClassName(mode === SHOW_MODE.LIST ? 'storage__list-item-object-name' : 'storage__files-item');
                    const foundElements = Array.from(elements).filter(element => {
                        return element.textContent.trim() === item.name;
                    });
                    foundElements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 500);
            }
        }
    }, [params, storageData, mode]);

    if (isFetched && !showAccessModal && !showReplaceModal && !showShareModal && !listzip && !activeListzip) {
        return <Spinner />;
    }

    if (!enabled) {
        return <StorageMyNotPermitted />;
    }

    return (
        <>
            <div className="storage__main">
                {needReply && <StorageReplyModal
                    isOpen={!!needReply}
                    onClose={() => setNeedRelpy(undefined)}
                    needReply={needReply}
                    onSuccess={() => {
                        const op = operations.find((z) => z.id === needReply.opId);
                        if (op) {
                            dispatchOperations({
                                ...op, type: 'changed', status: SdnOperationTaskStatus.SUCCESS,
                            });
                        }
                    }}
                />}
                <StorageReplaceFileModal
                    isOpen={showReplaceModal}
                    onClose={() => setShowReplaceModal(false)}
                    files={replaceFiles}
                    onReplace={() => {
                        uploadFiles(replaceFiles, true);
                    }}
                    onSaveBoth={() => {
                        uploadFiles(replaceFiles, false);
                    }}
                />
                {activeListzip && <Modal isOpen={activeListzip} onClose={() => setActiveListzip(false)}>
                    <Spinner />
                </Modal>}
                {showAccessModal && <StorageAccessModal isOpen={showAccessModal} path={accessPath} onClose={() => setShowAccessModal(false)} />}
                <StorageDeleteConfirmModal
                    isOpen={showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(false)}
                    mbDisclamer={folderPrefix.some((fdpx) => !!cardObjects[fdpx])}
                    mbShareDisclamer={folderPrefix.some((fdpx) => !!(myPermissions[fdpx]?.length > 0))}
                    mbAccessDisclamer={folderPrefix.some((fdpx) => !!(links[fdpx]?.length > 0))}
                    files={folderPrefix}
                    onDelete={() => {
                        deleteFiles(client, bucketname || '', folderPrefix).then(() => {
                            setShowDeleteConfirm(false);
                            setUpdate(update + 1);
                        }).catch((e) => alert.error(t(extractS3Error(e))));
                    }}
                />
                <StorageListZipModal isOpen={!!listzip} onClose={() => setListzip(null)} listzip={listzip} maxWidth={800} onExtract={(s) => unZipFileOperation(extractPrefix(listzip?.source.slice(5/* s3:// */) || ''), prefix || '', s)} />
                <StorageVideoModal isOpen={!!showPlayer} name={videoName} onClose={() => setShowPlayer('')} url={showPlayer} />
                {/* <StorageShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} prefix={preSignPrefix} bucketname={bucketname || ''} /> */}
                <StorageLinkModal
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                    prefix={preSignPrefix}
                    bucketname={bucketname || ''}
                />
                {metaReport && <StorageReportModal isOpen={isOpenMeta} onClose={() => setIsOpenMeta(false)} meta={metaReport} />}
                {isOpenMobileModal && <StorageFiltersModal isOpen={isOpenMobileModal} onClose={() => setIsOpenMobileModal(false)}>
                    <StorageFiltersMobileGroup label={t('Filters')} className="_filters">
                        <StorageFilterItem className="_type" placeholder={t('Type')} radio value={type} onChange={(v) => setType(v)} options={availTypes} />
                        {/* <StorageFilterPeople placeholder={t('People')} value={people} options={availPeople} onChange={(v) => setPeople(v)} /> */}
                        <StorageFilterItem placeholder={t('Updated')} value={date} onChange={(v) => setDate(v)} onChangeCustom={(since, till) => { setSinceDate(since); setTillDate(till); }} options={availDates} radio dateWithCustom />
                        <CheckBox label={t('Access')} value={access} onChange={(v) => setAccess(v)} />
                    </StorageFiltersMobileGroup>
                    <StorageFiltersMobileGroup label={t('component:VIEW')} className="_view">
                        <StorageView mode={mode} setMode={setMode} />
                    </StorageFiltersMobileGroup>
                    <StorageFiltersMobileGroup label={t('Sorting')} className="_sort">
                        <StorageSorter value={sort} options={availSort} onChange={(v) => setSort(v)} />
                    </StorageFiltersMobileGroup>
                </StorageFiltersModal>}
                {isOpen && <StorageFolderSelection
                    client={client}
                    bucketname={bucketname || ''}
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    prefix={folderPrefix}
                    initPath={(folderPrefix.length === 1 && folderMode === 'unzip') ? s3dirname(folderPrefix[0]) : ''}
                    mode={folderMode}
                    onAction={(p) => {
                        if (folderMode === 'copy') {
                            copyFilesOperation(folderPrefix, p);
                            // folderPrefix.forEach((src) => copyFileOperation(src, p));
                        } else if (folderMode === 'move') {
                            moveFilesOperation(folderPrefix, p);
                            // folderPrefix.forEach((src) => moveFileOperation(src, p));
                        } else {
                            folderPrefix.forEach((src) => unZipFileOperation(src, p));
                        }
                        setIsOpen(false);
                    }}
                />}

                <StorageModal
                    isOpen={isOpenCreateFolder}
                    onClose={() => setIsOpenCreateFolder(false)}
                    title={t('storage:FOLDER_NAME')}
                    occupied={storageData.filter((s) => s.folder).map((s) => s.name)}
                    onConfirm={(path) => {
                        if (path) {
                            const f = new File([''], '');
                            uploadFile(client, bucketname, `${prefix ? prefix : '/'}${path}/`, f, f.name, httpHandler, (p) => console.log(p))
                                .then(() => {
                                    setUpdate((p) => p + 1);
                                    navigate(`${location.pathname}${prefix ? '' : '/'}${encodeURIComponent(path)}%2f`);
                                    setIsOpenCreateFolder(false);
                                })
                                .catch((e) => {
                                    alert.error(extractErrorDetail(e));
                                });
                        }
                    }}
                />
                {folderPrefix.length > 0 && <StorageModal
                    isOpen={isOpenRenameFile}
                    onClose={() => setIsOpenRenameFile(false)}
                    title={t('storage:RENAME')}
                    initValue={getBasename(folderPrefix[0])}
                    occupied={storageData.map((s) => s.name)}
                    onConfirm={(path) => {
                        if (path) {
                            folderPrefix.forEach((src) => {
                                renameFileOperation(src, path);
                                setIsOpenRenameFile(false);
                            })
                        }
                    }}
                />}

                {folderPrefix.length > 0 && <StorageModal
                    isOpen={isOpenZipName}
                    onClose={() => setIsOpenZipName(false)}
                    title={t('storage:ZIP')}
                    initValue={getBasename(folderPrefix[0])}
                    occupied={storageData.map((s) => s.name + '.zip')}
                    onConfirm={(path) => {
                        if (path) {
                            zipFileOperation(folderPrefix, path);
                            setIsOpenZipName(false);
                        }
                    }}
                />}

                <div className="storage__main-inner">
                    <StorageBreadcrumb items={breadcrumbs}>
                        <StorageFilters
                            mobile={addBtn}
                            onMobile={() => setIsOpenMobileModal(true)}
                            rightSlot={<>
                                <StorageSorter value={sort} options={availSort} onChange={(v) => setSort(v)} />
                                <StorageView mode={mode} setMode={setMode} />
                            </>}
                        >
                            <StorageSearcher value={search} onChange={(v) => setSearch(v)} results={searchHints} />
                            {!isMobile && (<>
                                <StorageFilterItem className="_type" placeholder={t('Type')} radio value={type} onChange={(v) => setType(v)} options={availTypes} />
                                {/* <StorageFilterPeople placeholder={t('People')} value={people} options={availPeople} onChange={(v) => setPeople(v)} /> */}
                                <StorageFilterItem placeholder={t('Updated')} value={date} onChange={(v) => setDate(v)} onChangeCustom={(since, till) => { setSinceDate(since); setTillDate(till); }} options={availDates} radio dateWithCustom />
                                <CheckBox label={t('Access')} value={access} onChange={(v) => setAccess(v)} />
                            </>)}
                        </StorageFilters>
                    </StorageBreadcrumb>

                    <div className={`storage__main-body ${storageData.filter((s) => s.name).length ? '' : ' storage__empty'}`}>
                        {storageData.filter((s) => s.name).length === 0 ? <StorageMyEmpty /> :
                            (mode === SHOW_MODE.LIST ?
                                <StorageList
                                    keyField="prefix"
                                    selected={selected}
                                    setSelected={setSelected}
                                    menuFull
                                    onDoubleClick={async (row) => {
                                        if (row.path) {
                                            navigate(row.path);
                                        } else if (row.prefix && isArchive(row.name) && !activeListzip) {
                                            listZipFileOperation(row.prefix);
                                        } else if (row.prefix && PLAYED.some((ext) => row.prefix?.toLowerCase().endsWith(ext)) && row.size && row.size < (15 * 1024 * 1024 * 1024)) {
                                            setVideoName(row.name);
                                            createPreSignUrl(client, bucketname || '', row.prefix).then((z) => setShowPlayer(z));
                                        }
                                        setSearch('');
                                    }}
                                    columns={[
                                        {
                                            name: t('Title'), additionalClass: '_name', sortable: true, selector: (row) => row.name, format: (row) =>
                                                <div className={
                                                    'storage__list-item-object'
                                                    + (row.linked ? ' _mediabaza' : '')
                                                    + (row.folder ? ' _folder' : ' _file')
                                                    + ((isVideo(row.name) || isAudio(row.name)) ? ' _video' : '')
                                                    + (isArchive(row.name) ? ' _archive' : '')
                                                }>
                                                    <div className="storage__list-item-object-icon"></div>
                                                    <div className="storage__list-item-object-name">
                                                        {row.name}
                                                        {/* {row.path ?
                                                            <Link to={row.path}>{row.name}</Link>
                                                            :
                                                            row.name} */}
                                                    </div>
                                                </div>,
                                            onHeaderClick: () => sort === 'name' ? setSort('-name') : setSort('name')
                                        },
                                        { name: t('storage:OPERATIONS'), additionalClass: '_processing', selector: (row) => row.operations, },
                                        {
                                            name: t('Downloads'), additionalClass: '_downloads', selector: (row) =>
                                                !row.prefix?.endsWith('/') ? counters[`${bucketname}/${row.prefix}` || ''] || 0 : ''
                                        },
                                        {
                                            name: t('Access'), additionalClass: '_access', selector: () => '', format: (row) => {
                                                return <>
                                                    {row.access &&
                                                        <div className="storage__list-item-share" title="" onClick={(e) => {
                                                            e.preventDefault();
                                                            setPreSignPrefix(row.prefix || ''); setShowShareModal(true);
                                                        }}>
                                                            <ShareIcon />
                                                        </div>
                                                    }
                                                    {row.shared &&
                                                        // <Tippy
                                                        //     content={<div className="storage__files-item-share-access">
                                                        //         <div className="storage__files-item-share-access-label">
                                                        //             <Trans>storage:ACCESS_SHARE_OPEN</Trans>
                                                        //         </div>
                                                        //     </div>}
                                                        //     placement="right-end"
                                                        //     animation="shift-away"
                                                        //     theme="storage"
                                                        //     arrow={false}
                                                        //     duration={[100, 100]}
                                                        //     allowHTML={true}
                                                        //     interactive={true}
                                                        //     appendTo={() => document.body}
                                                        // >
                                                        <div className="storage__list-item-access" onClick={() => {
                                                            if ((myObjects[row.prefix || ''] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL) {
                                                                alert.error(t('Access denied'));
                                                            } else {
                                                                setShowAccessModal(true); setAccessPath(row.prefix || '');
                                                            }
                                                        }}>
                                                            <AccessIcon />
                                                        </div>
                                                        // </Tippy>
                                                    }
                                                </>
                                            }
                                        },
                                        {
                                            name: t('storage:CHECK'), additionalClass: '_reports', selector: () => '', format: (row) => (
                                                metaObjects[row.prefix || ''] && <div className="storage__list-col _reports">
                                                    <button
                                                        className="storage__list-item-reports"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            if (row.prefix) {
                                                                setIsOpenMeta(true);
                                                                setMetaReport(metaObjects[row.prefix])
                                                            }
                                                        }}
                                                    >
                                                        <img src={CheckOk} alt="" /><Trans>storage:REPORTS</Trans>
                                                    </button>
                                                </div>
                                            )
                                        },
                                        { name: t('Size'), additionalClass: '_size', selector: (row) => row.size || '', format: (row) => row.size && formatBytes(row.size), onHeaderClick: () => sort === 'size' ? setSort('-size') : setSort('size') },
                                        { name: t('storage:MOD_TIME'), additionalClass: '_date', selector: (row) => row.name, format: (row) => toMplatBackendDate(row.modTime), onHeaderClick: () => sort === 'date' ? setSort('-date') : setSort('date') },
                                    ]}
                                    data={storageData.filter((s) => s.name && getAnyPerm(s.prefix)).filter(searchFilter).filter(typeFilter).filter(accessFilter).filter(dateFilter)}
                                    isFolderRow={(row) => row.folder}
                                    // isActiveRow={(row) => { return selected.includes(row.prefix || ''); }}
                                    isInProgress={inProgress}
                                    onDownloadZip={onDownloadZip}
                                    onDownload={(s) => s.forEach(v => downloadFile(client, bucketname || '', v))}
                                    onDelete={(s) => { setShowDeleteConfirm(true); setFolderPrefix(s); }}
                                    onShare={haveFullAccess ? (s) => {
                                        if ((myObjects[s] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL) {
                                            alert.error(t('Access denied'));
                                        } else {
                                            setPreSignPrefix(s); setShowShareModal(true);
                                        }
                                    } : undefined}
                                    onRename={(s) => { setIsOpenRenameFile(true); setFolderPrefix([s]) }}
                                    onUnzip={(s) => { setIsOpen(true); setFolderPrefix([s]); setFolderMode('unzip'); }}
                                    onZip={availUpload ? (s) => { setIsOpenZipName(true); setFolderPrefix(s) } : undefined}
                                    onCopy={(s) => { setIsOpen(true); setFolderPrefix(s); setFolderMode('copy'); }}
                                    onMove={(s) => { setIsOpen(true); setFolderPrefix(s); setFolderMode('move'); }}
                                    onAccess={haveFullAccess ? (s) => {
                                        if ((myObjects[s] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL) {
                                            alert.error(t('Access denied'));
                                        } else {
                                            setShowAccessModal(true); setAccessPath(s);
                                        }
                                    } : undefined}
                                >
                                    <div style={{ height: '54px' }} />
                                </StorageList>
                                :
                                <StorageGrid
                                    selected={selected}
                                    setSelected={setSelected}
                                    menuFull
                                    onDoubleClick={(row) => {
                                        if (row.path) {
                                            navigate(row.path);
                                        } else if (row.prefix && isArchive(row.name) && !activeListzip) {
                                            listZipFileOperation(row.prefix);
                                        } else if (row.prefix && PLAYED.some((ext) => row.prefix?.toLowerCase().endsWith(ext)) && row.size && row.size < (15 * 1024 * 1024 * 1024)) {
                                            setVideoName(row.name);
                                            createPreSignUrl(client, bucketname || '', row.prefix).then((z) => setShowPlayer(z));
                                        }
                                        setSearch('');
                                    }}
                                    data={storageData.filter((s) => s.name && getAnyPerm(s.prefix)).filter(searchFilter).filter(typeFilter).filter(accessFilter).filter(dateFilter)}
                                    forbidDelete={selected.some((s) => (myObjects[s] & SdnPermissionType.WRITE) !== SdnPermissionType.WRITE)}
                                    onDownloadZip={onDownloadZip}
                                    onDownload={(s) => s.forEach(v => downloadFile(client, bucketname || '', v))}
                                    onDelete={(s) => { setShowDeleteConfirm(true); setFolderPrefix(s); }}
                                    onShare={haveFullAccess ? (s) => {
                                        if ((myObjects[s] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL) {
                                            alert.error(t('Access denied'));
                                        } else {
                                            setPreSignPrefix(s); setShowShareModal(true);
                                        }
                                    } : undefined}
                                    onRename={(s) => { setIsOpenRenameFile(true); setFolderPrefix([s]) }}
                                    onUnzip={(s) => { setIsOpen(true); setFolderPrefix([s]); setFolderMode('unzip'); }}
                                    onZip={availUpload ? (s) => { setIsOpenZipName(true); setFolderPrefix(s) } : undefined}
                                    onCopy={(s) => { setIsOpen(true); setFolderPrefix(s); setFolderMode('copy'); }}
                                    onMove={(s) => { setIsOpen(true); setFolderPrefix(s); setFolderMode('move'); }}
                                    onAccess={haveFullAccess ? (s) => {
                                        if ((myObjects[s] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL) {
                                            alert.error(t('Access denied'));
                                        } else {
                                            setShowAccessModal(true); setAccessPath(s);
                                        }
                                    } : undefined}
                                />)}
                    </div>
                    {availUpload && addBtn}
                    <StorageNotifications />
                </div>
            </div>
            {selected.length > 0 && <StorageActions
                client={client}
                selected={selected}
                forbidDelete={selected.some((s) => (myObjects[s] & SdnPermissionType.WRITE) !== SdnPermissionType.WRITE)}
                onClean={() => setSelected([])}
                onDownloadZip={onDownloadZip}
                onDownload={(s) => s.forEach(v => downloadFile(client, bucketname || '', v))}
                onDelete={(s) => { setShowDeleteConfirm(true); setFolderPrefix(s); }}
                onShare={haveFullAccess ? (s) => {
                    if ((myObjects[s] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL) {
                        alert.error(t('Access denied'));
                    } else {
                        setPreSignPrefix(s); setShowShareModal(true);
                    }
                } : undefined}
                onAccess={haveFullAccess ? (s) => {
                    if (selected.some((ss) => (myObjects[ss] & SdnPermissionType.FULLCONTROL) !== SdnPermissionType.FULLCONTROL)) {
                        alert.error(t('Access denied'));
                    } else {
                        setShowAccessModal(true); setAccessPath(s);
                    }
                } : undefined}
                onCopy={(s) => { setIsOpen(true); setFolderPrefix(s); setFolderMode('copy'); }}
                onMove={availUpload ? (s) => { setIsOpen(true); setFolderPrefix(s); setFolderMode('move'); } : undefined}
                onUnzip={availUpload ? (s) => { setIsOpen(true); setFolderPrefix([s]); setFolderMode('unzip'); } : undefined}
                onZip={availUpload ? (s) => { setIsOpenZipName(true); setFolderPrefix(s) } : undefined}
                onRename={availUpload ? (s) => { setIsOpenRenameFile(true); setFolderPrefix([s]) } : undefined}
            />}
        </>
    );
})

export default StorageMy;
